import { useEffect, useRef } from "react";
import "./PrivacyPolicy.css";

const SECTIONS = [
  {
    id: 1,
    title: "Information We Collect",
    points: [
      "When you create a profile, we may collect personal information such as your name, age, gender, contact details, location, religion, education, profession, and family details.",
      "We also collect photographs, partner preferences, and other information required to help you discover compatible matches.",
      "Technical information such as device details, browser information, IP address, login activity, and usage behavior may be collected to improve the overall platform experience and ensure account security.",
    ],
  },
  {
    id: 2,
    title: "How We Use Your Information",
    points: [
      "The information you provide is primarily used to create and manage your profile, suggest relevant matches, and improve user experience.",
      "We use your data to provide customer support, maintain platform security, and communicate important updates related to your account and activities.",
      "Our goal is to offer a trusted matrimony environment where users can connect genuinely and confidently.",
    ],
  },
  {
    id: 3,
    title: "Your Privacy Controls",
    points: [
      "Niran Matrimony respects your privacy choices and provides controls that allow you to manage the visibility of your information.",
      "You may choose to hide personal details, restrict photo visibility, block users, or control who can access certain parts of your profile.",
      "Some information may only be visible to verified or matched users in order to maintain safety and privacy standards on the platform.",
    ],
  },
  {
    id: 4,
    title: "Data Sharing & Security",
    points: [
      "We do not sell, rent, or trade your personal information to third parties.",
      "Your information is shared only within the platform according to your privacy settings or when legally required to comply with applicable laws, legal processes, or security obligations.",
      "We take reasonable and appropriate security measures to protect user information from unauthorized access, misuse, loss, or disclosure.",
    ],
  },
  {
    id: 5,
    title: "Platform Safety",
    points: [
      "To maintain a safe and trustworthy community, Niran Matrimony may review profiles and monitor suspicious activities.",
      "We take action against fake, misleading, abusive, or fraudulent accounts.",
      "Users who violate platform guidelines or misuse the service may face restrictions, suspension, or permanent removal from the platform.",
    ],
  },
  {
    id: 6,
    title: "Cookies & Technologies",
    points: [
      "Our platform may use cookies and similar technologies to improve website functionality and remember preferences.",
      "These technologies help us analyze user interactions, enhance browsing experience, and provide smoother and more personalized services to our users.",
    ],
  },
  {
    id: 7,
    title: "Communications",
    points: [
      "Niran Matrimony may occasionally send account-related notifications, match updates, interest alerts, security messages, and customer support communications.",
      "Users may manage certain notification preferences through their account settings.",
    ],
  },
  {
    id: 8,
    title: "Account Deletion",
    points: [
      "You may choose to edit, deactivate, or permanently delete your profile at any time.",
      "Upon account deletion, certain information may be removed from our active systems, subject to legal, regulatory, or security obligations where applicable.",
    ],
  },
  {
    id: 9,
    title: "Eligibility",
    points: [
      "Niran Matrimony is intended only for individuals who are legally eligible for marriage under applicable laws.",
      "We do not knowingly collect or maintain information from minors or unauthorized users.",
    ],
  },
  {
    id: 10,
    title: "Policy Updates",
    points: [
      "We may update this Privacy Policy from time to time to reflect improvements to our platform, changes in regulations, or updated security practices.",
      "Continued use of Niran Matrimony after such updates indicates acceptance of the revised policy.",
    ],
  },
];

export default function PrivacyPolicy() {
  const heroRef = useRef(null);
  const introRef = useRef(null);
  const bodyRef = useRef(null);
  const closingRef = useRef(null);

  useEffect(() => {
    const items = [
      { el: heroRef.current, delay: 0 },
      { el: introRef.current, delay: 0.12 },
      { el: bodyRef.current, delay: 0.22 },
      { el: closingRef.current, delay: 0.1 },
    ];

    const observers = items.map(({ el, delay }) => {
      if (!el) return null;

      el.style.setProperty("--entry-delay", `${delay}s`);

      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            el.classList.add("pp-visible");
            obs.disconnect();
          }
        },
        { threshold: 0.08 },
      );

      obs.observe(el);
      return obs;
    });

    return () => observers.forEach((o) => o && o.disconnect());
  }, []);

  return (
    <div className="pp-page">
      <div className="pp-top-bar" />

      {/* Hero */}
      <div className="pp-hero pp-entry" ref={heroRef}>
        <p className="pp-hero__eyebrow">Niran Matrimony</p>
        <h1 className="pp-hero__title">Privacy Policy</h1>
        <div className="pp-hero__line" />
      </div>

      <div className="pp-container">
        {/* Intro */}
        <p className="pp-intro pp-entry" ref={introRef}>
          At Niran Matrimony, we believe that finding a life partner is one of
          the most personal and meaningful journeys in life. Every detail you
          share with us is treated with care, respect, and responsibility. Your
          trust is important to us, and we are committed to protecting your
          privacy while creating a safe and genuine platform for meaningful
          connections.
        </p>

        {/* Main Body */}
        <div className="pp-body pp-entry" ref={bodyRef}>
          {SECTIONS.map((sec, i) => (
            <div key={sec.id} className="pp-block">
              {i !== 0 && <div className="pp-block__divider" />}

              <div className="pp-block__heading">
                <h2 className="pp-block__title">{sec.title}</h2>
              </div>

              <ul className="pp-list">
                {sec.points.map((point, j) => (
                  <li key={j} className="pp-list__item">
                    <span className="pp-list__text">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Closing */}
        <div className="pp-closing pp-entry" ref={closingRef}>
          <div className="pp-closing__rule" />

          <p className="pp-closing__contact">
            If you have any questions, concerns, or privacy-related requests,
            you may contact us at{" "}
            <a
              href="mailto:support@niranmatrimony.com"
              className="pp-closing__link"
            >
              support@niranmatrimony.com
            </a>
          </p>

          <p className="pp-closing__tagline">
            At Niran Matrimony, privacy is not just a legal requirement — it is
            a commitment to every individual and family who trusts us with their
            journey. Your safety, comfort, and confidence will always remain at
            the heart of everything we do.
          </p>
        </div>
      </div>

      <div className="pp-top-bar pp-bottom-bar" />
    </div>
  );
}
